/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  Content,
  GoogleGenAI,
  LiveCallbacks,
  LiveClientToolResponse,
  LiveConnectConfig,
  LiveServerContent,
  LiveServerMessage,
  LiveServerToolCall,
  LiveServerToolCallCancellation,
  Part,
  Session,
} from "@google/genai";

import { EventEmitter } from "eventemitter3";
import { difference } from "lodash";
import { LiveClientOptions, StreamingLog, Transcription } from "../types";
import { getStoredToken } from "../services/authService";
import { logConversation } from "../services/api";
import { base64ToArrayBuffer } from "./utils";

/**
 * Event types that can be emitted by the MultimodalLiveClient.
 * Each event corresponds to a specific message from GenAI or client state change.
 */
export interface LiveClientEventTypes {
  // Emitted when audio data is received
  audio: (data: ArrayBuffer) => void;
  // Emitted when the connection closes
  close: (event: CloseEvent) => void;
  // Emitted when content is received from the server
  content: (data: LiveServerContent) => void;
  // Emitted when an error occurs
  error: (error: ErrorEvent) => void;
  // Emitted when the server interrupts the current generation
  interrupted: () => void;
  // Emitted for logging events
  log: (log: StreamingLog) => void;
  // Emitted when the connection opens
  open: () => void;
  // Emitted when the initial setup is complete
  setupcomplete: () => void;
  // Emitted when a tool call is received
  toolcall: (toolCall: LiveServerToolCall) => void;
  // Emitted when a tool call is cancelled
  toolcallcancellation: (
    toolcallCancellation: LiveServerToolCallCancellation
  ) => void;
  // Emitted when the current turn is complete
  turncomplete: () => void;
  // Emitted when the conversation is finished
  conversationend: () => void;
  // Emitted when the server sends a goAway message
  goaway: (event: any) => void;
  // Emitted when session resumption token is updated
  sessionresumptionupdate: (event: any) => void;
}

/**
 * A event-emitting class that manages the connection to the websocket and emits
 * events to the rest of the application.
 * If you dont want to use react you can still use this.
 */
export class GenAILiveClient extends EventEmitter<LiveClientEventTypes> {
  protected client?: GoogleGenAI;
  private clientOptions: LiveClientOptions;
  public lastUserActivity: number = Date.now();

  private _setupComplete: boolean = false;
  public get setupComplete() {
    return this._setupComplete;
  }

  private _status: "connected" | "disconnected" | "connecting" = "disconnected";
  public get status() {
    return this._status;
  }

  private _session: Session | null = null;
  public get session() {
    return this._session;
  }

  private _model: string | null = null;
  public get model() {
    return this._model;
  }

  private sessionId: string | null = null;

  public setSessionId(id: string) {
    this.sessionId = id;
  }

  private currentModelResponse: string = "";
  private currentUserResponse: string = "";

  public transcriptions: {
    conversationTurns: { speaker: "user" | "model"; text: string }[];
    notes: string[];
  } = {
      conversationTurns: [],
      notes: [],
    };

  protected config: LiveConnectConfig | null = null;

  public getConfig() {
    return { ...this.config };
  }

  constructor(options: LiveClientOptions) {
    super();
    this.clientOptions = options;
    this.send = this.send.bind(this);
    this.onopen = this.onopen.bind(this);
    this.onerror = this.onerror.bind(this);
    this.onclose = this.onclose.bind(this);
    this.onmessage = this.onmessage.bind(this);
  }

  protected log(type: string, message: StreamingLog["message"]) {
    const log: StreamingLog = {
      date: new Date(),
      type,
      message,
    };
    this.emit("log", log);
  }

  async connect(
    model: string,
    config: LiveConnectConfig,
    apiKey: string
  ): Promise<boolean> {
    if (this._status === "connected" || this._status === "connecting") {
      return false;
    }

    // Initialize the client now that we have the key
    this.clientOptions.apiKey = apiKey;
    this.client = new GoogleGenAI(this.clientOptions);

    this._status = "connecting";
    this.config = config;
    this._model = model;
    this._setupComplete = false;

    const callbacks: LiveCallbacks = {
      onopen: this.onopen,
      onmessage: this.onmessage,
      onerror: this.onerror,
      onclose: this.onclose,
    };

    try {
      this._session = await this.client.live.connect({
        model,
        config,
        callbacks,
      });
      console.log(
        "GenAILiveClient: connection successful, session:",
        this._session
      );
    } catch (e) {
      console.error("Error connecting to GenAI Live:", e);
      this._status = "disconnected";
      return false;
    }

    this._status = "connected";
    this.startTime = Date.now();

    // Create the conversation log entry on the backend
    this.logConversation(null, false);

    return true;
  }

  public disconnect(isComplete: boolean = true) {
    console.info("[GenAI Live] disconnect() called", {
      ts: new Date().toISOString(),
      model: this._model,
      sessionId: this.sessionId,
      conversationId: this.conversationId,
      status: this._status,
    });

    if (!this.session) {
      return false;
    }
    this.session?.close();
    this._session = null;
    this._status = "disconnected";
    this.client = undefined;
    this._setupComplete = false;

    // Send final backlog and mark as complete ONLY if explicitly requested
    this.logConversation(null, isComplete);

    this.log("client.close", `Disconnected`);
    return true;
  }

  protected onopen() {
    console.info("[GenAI Live] WS OPEN", {
      model: this._model,
      sessionId: this.sessionId,
      conversationId: this.conversationId,
      ts: new Date().toISOString(),
    });
    this.log("client.open", "Connected");
    this.emit("open");
  }

  protected onerror(e: ErrorEvent) {
    console.error("[GenAI Live] WS ERROR", {
      message: e.message,
      filename: (e as any).filename,
      lineno: (e as any).lineno,
      colno: (e as any).colno,
      ts: new Date().toISOString(),
      model: this._model,
      sessionId: this.sessionId,
      conversationId: this.conversationId,
    });
    this.log("server.error", e.message);
    this.emit("error", e);
  }

  private conversationId: string | null = null;
  private startTime: number | null = null;

  protected onclose(e: CloseEvent) {
    console.warn("[GenAI Live] WS CLOSE", {
      code: e.code,
      reason: e.reason,
      wasClean: e.wasClean,
      ts: new Date().toISOString(),
      model: this._model,
      sessionId: this.sessionId,
      conversationId: this.conversationId,
    });
    this.log(
      `server.close`,
      `disconnected ${e.reason ? `with reason: ${e.reason}` : ``}`
    );

    // Update internal state to disconnected
    this._status = "disconnected";
    this._session = null;
    this.client = undefined;
    this._setupComplete = false;

    // Send final backlog but DO NOT mark as complete automatically on socket close.
    // This allows for auto-resume logic to kick in without killing the backend session.
    this.logConversation(null, false);

    this.emit("close", e);
  }

  /**
   * Sends a single conversation turn or a completion event to the backend.
   */
  protected async logConversation(
    turn: { speaker: "user" | "model"; text: string } | null,
    isComplete: boolean = false
  ) {
    const notesText = this.transcriptions.notes.join(" ").trim();

    const payload: Record<string, any> = {
      session_id: this.sessionId,
      conversation_id: this.conversationId,
      transcript_chunk: turn ? [turn] : [],
      notes: notesText,
      is_complete: isComplete,
    };

    if (isComplete) {
      let userWords = 0;
      let modelWords = 0;
      this.transcriptions.conversationTurns.forEach((t) => {
        const wordCount = t.text.split(/\s+/).filter(Boolean).length;
        if (t.speaker === "user") userWords += wordCount;
        else modelWords += wordCount;
      });

      payload.total_words = userWords + modelWords;
      payload.user_words = userWords;
      payload.model_words = modelWords;
      payload.duration_seconds = this.startTime
        ? (Date.now() - this.startTime) / 1000
        : 0;
    }

    const token = getStoredToken();
    if (!token) {
      console.error("No auth token found for backlog request");
      return;
    }

    try {
      const data = await logConversation(payload, token);
      if (data.conversation_id && !this.conversationId) {
        this.conversationId = data.conversation_id;
      }
      if (isComplete) {
        this.conversationId = null;
        this.transcriptions = { conversationTurns: [], notes: [] };
      }
    } catch (error) {
      console.error("Error sending conversation log:", error);
    }
  }

  protected async sendUserTranscript() {
    if (this.currentUserResponse) {
      const newTurn = {
        speaker: "user" as const,
        text: this.currentUserResponse.trim(),
      };
      this.transcriptions.conversationTurns.push(newTurn);
      this.logConversation(newTurn);
      this.currentUserResponse = "";
    }
  }

  protected async onmessage(message: LiveServerMessage) {
    if (message.setupComplete) {
      this._setupComplete = true;
      this.log("server.send", "setupComplete");
      this.emit("setupcomplete");
      return;
    }
    if (message.goAway) {
      this.log("server.goAway", message.goAway);
      this.emit("goaway", message.goAway);
      console.log("Received goAway message from server:", message.goAway);
      return;
    }
    if (message.sessionResumptionUpdate) {
      this.log("server.sessionResumptionUpdate", message.sessionResumptionUpdate);
      this.emit("sessionresumptionupdate", message.sessionResumptionUpdate);
      return;
    }
    if (message.toolCall) {
      console.log(
        "DEBUG: Tool call received from model:",
        JSON.stringify(message.toolCall, null, 2)
      );
      this.log("server.toolCall", message);
      this.emit("toolcall", message.toolCall);
      this.sendUserTranscript();
      return;
    }
    if (message.toolCallCancellation) {
      this.log("server.toolCallCancellation", message);
      this.emit("toolcallcancellation", message.toolCallCancellation);
      return;
    }

    if (message.serverContent) {
      const { serverContent } = message;
      if ("interrupted" in serverContent) {
        this.log("server.content", "interrupted");
        this.emit("interrupted");
        return;
      }

      // User speech transcription
      if (serverContent.inputTranscription) {
        const userText: Transcription = serverContent.inputTranscription;
        if (userText.text) {
          this.currentUserResponse += userText.text;
          this.lastUserActivity = Date.now();
        }
      }

      // Model speech transcription
      if (serverContent.outputTranscription) {
        const modelText: Transcription = serverContent.outputTranscription;
        if (modelText.text) {
          this.currentModelResponse += modelText.text;
        }
        this.sendUserTranscript();
      }

      if (serverContent.generationComplete) {
        if (this.currentModelResponse) {
          const newTurn = {
            speaker: "model" as const,
            text: this.currentModelResponse.trim(),
          };
          this.transcriptions.conversationTurns.push(newTurn);
          this.logConversation(newTurn);
          this.currentModelResponse = "";
        }
        this.log("server.content", "turnComplete");
        this.emit("turncomplete");
      }

      // Audio from the model
      if ("modelTurn" in serverContent) {
        let parts: Part[] = serverContent.modelTurn?.parts || [];
        const audioParts = parts.filter(
          (p) => p.inlineData && p.inlineData.mimeType?.startsWith("audio/pcm")
        );
        const base64s = audioParts.map((p) => p.inlineData?.data);
        const otherParts = difference(parts, audioParts);

        base64s.forEach((b64) => {
          if (b64) {
            const data = base64ToArrayBuffer(b64);
            this.emit("audio", data);
            this.log(`server.audio`, `buffer (${data.byteLength})`);
          }
        });
        if (!otherParts.length) return;

        parts = otherParts;
        const content: { modelTurn: Content } = { modelTurn: { parts } };
        this.emit("content", content);
        this.log(`server.content`, message);
      }
    } else {
      console.log("received unmatched message", message);
    }
  }

  /**
   * send realtimeInput, this is base64 chunks of "audio/pcm" and/or "image/jpg"
   */
  sendRealtimeInput(chunks: Array<{ mimeType: string; data: string }>) {
    if (!this._setupComplete) return;
    let hasAudio = false;
    let hasVideo = false;
    for (const ch of chunks) {
      this.session?.sendRealtimeInput({ media: ch });
      if (ch.mimeType.includes("audio")) {
        hasAudio = true;
      }
      if (ch.mimeType.includes("image")) {
        hasVideo = true;
      }
      if (hasAudio && hasVideo) {
        break;
      }
    }
    const message =
      hasAudio && hasVideo
        ? "audio + video"
        : hasAudio
          ? "audio"
          : hasVideo
            ? "video"
            : "unknown";
    this.log(`client.realtimeInput`, message);
    this.lastUserActivity = Date.now();
  }

  /**
   *  send a response to a function call and provide the id of the functions you are responding to
   */
  sendToolResponse(toolResponse: LiveClientToolResponse) {
    if (
      toolResponse.functionResponses &&
      toolResponse.functionResponses.length
    ) {
      this.session?.sendToolResponse({
        functionResponses: toolResponse.functionResponses,
      });
      this.log(`client.toolResponse`, toolResponse);
      this.lastUserActivity = Date.now();
    }
  }

  /**
   * send normal content parts such as { text }
   */
  send(parts: Part | Part[], turnComplete: boolean = true) {
    this.session?.sendClientContent({ turns: parts, turnComplete });
    this.log(`client.send`, {
      turns: Array.isArray(parts) ? parts : [parts],
      turnComplete,
    });
    this.lastUserActivity = Date.now();
  }
}
